// KopfDokumentLeiste.tsx — die Dokument-Chips im neuen Aktenkopf (User 27.09.2026):
//
//   |Kundenerstinformation ●|weitere Vollmachten ▾| … |sonstige Kontaktdokumente ▾|
//
// Kundenerstinformation: grüner Punkt, wenn eine hochgeladene oder
// unterschriebene Fassung vorliegt, sonst rot — nur bei Kunde/Interessent.
// „weitere Vollmachten": Menü in zwei Ebenen wie in Microsoft-Programmen —
// erste Ebene je Vollmacht mit grünem/rotem Punkt, zweite Ebene Herunterladen,
// Hochladen, Unterschreiben lassen. Fehlt die Vollmacht am Kontakt noch, legt
// die erste Aktion sie an — aber nur mit Schreibrecht (das Schloss der Akte gilt
// nur fürs Formular; Dokumente werden sofort abgelegt, Hotfix 28.09.2026)
// (`editable`). Sonst ist „Herunterladen" für eine fehlende (oder noch nie
// erzeugte) Vollmacht gesperrt: Der Abruf würde sie in der Akte anlegen. „Unterschreiben lassen" öffnet einen vorhandenen,
// gültigen Link direkt in einem neuen Fenster; sonst wird ein neuer erzeugt und
// im eigenen Popup gezeigt (`SignierlinkDialog`, User 28.09.2026).
// „sonstige Kontaktdokumente": Protokoll hochladen und der Sprung in den
// Reiter „Dokumente".
//
// „Hochladen" einer Vollmacht öffnet die KMVS-Dokumentauswahl
// (Kundendokumente · Aus Vorgängen · Vom Rechner, T-42) statt des
// Windows-Explorers. Ein vorhandenes Dokument wird nur verknüpft, nicht
// kopiert, und behält seine Kategorie; eine Datei vom Rechner wird wie bisher
// unter „Vollmachten/<Ordner>" abgelegt. Der Kunde ist dabei fest: Das
// Backend verknüpft nur Dokumente, die in der Akte dieses Kontakts stehen
// (direkt oder über einen Vorgang des Kunden, T-17).
//
// T-17: Die Kundenerstinformation zählt auch den Altbestand (`ContactAuthority`:
// „liegt vor" plus Scan) — angezeigt, nicht überführt (`erstinfoNachweis`).
//
// „Vollmachten ansehen" (PO 01.10.2026, ersetzt „Nachweis öffnen"): listet alle
// vorliegenden Vollmachten des Kontakts und seines Verbunds; ein Klick öffnet
// das Dokument im KMVS-PDF-Viewer in einem neuen Tab (`vollmachtNachweise`).

import * as React from "react";
import {
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tooltip,
} from "@fluentui/react-components";
import { VOLLMACHT_STANDARD_KEY } from "@kmvs/forms-core";
import { vollmachtService, blobHerunterladen, VollmachtFunktionFehlt } from "../../services/vollmachtService";
import { documentService } from "../../services/documentService";
import {
  VOLLMACHT_OPTIONEN,
  vollmachtDateiname,
  vollmachtLabel,
  type VollmachtDto,
} from "../vollmachten/vollmachtTypes";
import { SignierlinkDialog } from "../vollmachten/SignierlinkDialog";
import { apiUrl } from "../../utils/api";
import type { ContactAuthority } from "../../types/Contact";
import { erstinfoNachweis, erstinfoTooltip } from "./erstinfoNachweis";
import { nachweisText, nachweiseAusVollmachten, type VollmachtNachweis } from "./vollmachtNachweise";
import { imEditorTabOeffnen } from "../documents/pdf-editor/editorTabOeffnen";
import { fetchUnternehmensverbund } from "./table/useUnternehmensverbund";
import { mitgliederAusUmfeld } from "../vollmachten/erstinfoSammel";
import { VollmachtAuswahlDialog } from "./VollmachtAuswahlDialog";
import { auswahlHatInhalt, vollmachtAusAuswahlVerknuepfen } from "../vollmachten/vollmachtAusAuswahl";
import type { DokumentAuswahlErgebnis } from "../documents/auswahl/DokumentAuswahl";
import { SAMMEL_LABEL } from "../vollmachten/erstinfoSammel";
import { sammelOptionText, useErstinfoSammel } from "../vollmachten/useErstinfoSammel";
import {
  unterschreibenGesperrt,
  unterschreibenLassen,
  type SignierlinkAnzeige,
} from "../vollmachten/signierlink";

const liegtVor = (v?: VollmachtDto): boolean => v?.status === "hochgeladen" || v?.status === "unterschrieben";

const Punkt: React.FC<{ ok: boolean | null }> = ({ ok }) => (
  <span
    aria-hidden="true"
    style={{
      display: "inline-block",
      width: 8,
      height: 8,
      borderRadius: 999,
      marginRight: 6,
      flex: "0 0 auto",
      background: ok === null ? "var(--ink-4, #c7bfc6)" : ok ? "#2e8b57" : "#c0392b",
    }}
  />
);

/**
 * „Herunterladen" gesperrt? Nur dann, wenn der Abruf etwas in die Akte
 * schreiben müsste (Vollmacht fehlt oder ist noch nie erzeugt worden) und der
 * Anwender das nicht darf — kein Schreibrecht. Eine
 * vorhandene Fassung darf jeder herunterladen, der die Akte sieht.
 */
export function herunterladenGesperrt(v: VollmachtDto | undefined, editable: boolean): boolean {
  if (editable) return false;
  return !v || v.status === "offen";
}

/**
 * Öffnet eine geschützte API-Ressource in einem neuen Tab (Blob-Weg wie
 * `ContactAuthorityChip`): `window.open` liefe am Bearer-Token vorbei. Bewusst
 * NICHT über `useOpenDocument` — das zöge pdfjs in den Kopf jeder Akte.
 */
async function oeffneAuthentifiziert(pfad: string): Promise<void> {
  const res = await fetch(apiUrl(pfad));
  if (!res.ok) throw new Error(`Nachweis konnte nicht geöffnet werden (${res.status}).`);
  const objectUrl = URL.createObjectURL(await res.blob());
  const geoeffnet = window.open(objectUrl, "_blank", "noopener,noreferrer");
  if (geoeffnet) window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

/**
 * Öffnet einen Vollmacht-Nachweis in einem neuen Tab: ein Dokument im
 * KMVS-PDF-Viewer (`/dokumente/:id/pdf`), nicht als nackte Datei im
 * Browser-PDF. `window.open` synchron im Klick, sonst greift der Popup-Blocker.
 * Nur ein Altbestand-Scan ohne Dokument (Medienpfad) geht über den Blob-Tab.
 */
export function nachweisOeffnen(n: VollmachtNachweis): Promise<void> {
  if ("documentId" in n.ziel) {
    // Über den Editor-Tab (04.10.2026): ist schon einer offen, kommt der Nachweis
    // dort als weiterer Tab dazu; sonst geht synchron ein neuer Browser-Tab auf.
    return imEditorTabOeffnen(n.ziel.documentId);
  }
  return oeffneAuthentifiziert(n.ziel.url.replace(/^\/+/, ""));
}

export const HERUNTERLADEN_GESPERRT_HINWEIS =
  "Diese Vollmacht liegt noch nicht vor. Zum Anlegen ist Schreibrecht an diesem Kontakt nötig.";


export function KopfDokumentLeiste({
  contactId,
  zeigeErstinfo,
  editable,
  onFehler,
  nachOrganigramm,
  onDokumenteOeffnen,
  kontaktName,
  unternehmen,
  authority,
}: {
  contactId: number;
  /** Nur bei Kunde/Interessent. */
  zeigeErstinfo: boolean;
  editable: boolean;
  onFehler: (text: string) => void;
  /** Der Organigramm-Chip der Akte — steht zwischen den Vollmachten und den Dokumenten. */
  nachOrganigramm?: React.ReactNode;
  onDokumenteOeffnen: () => void;
  /** Für Dateinamen und Meldung von „Für alle im … generieren". */
  kontaktName?: string;
  /** Bei einer Person: ihre Firmen („Verbindung zu Unternehmen") — zählen zum Verbund. */
  unternehmen?: Array<{ id: number; name: string }>;
  /** Altbestand der Erstinformation (`ContactAuthority`) — nur Anzeige (T-17). */
  authority?: ContactAuthority | null;
}) {
  const [liste, setListe] = React.useState<VollmachtDto[] | null>(null);
  const [nichtVerfuegbar, setNichtVerfuegbar] = React.useState(false);
  const dateiRef = React.useRef<HTMLInputElement | null>(null);
  /** Vollmacht, für die gerade die Dokumentauswahl offen ist (T-42). */
  const [auswahlArt, setAuswahlArt] = React.useState<string | null>(null);
  const [signierlink, setSignierlink] = React.useState<SignierlinkAnzeige | null>(null);
  const signierlinkSchliessen = React.useCallback(() => setSignierlink(null), []);
  const sammel = useErstinfoSammel(contactId, onFehler, { aktiv: zeigeErstinfo, name: kontaktName, unternehmen });

  // Vorliegende Vollmachten im Verbund — erst beim Öffnen eines Vollmacht-Chips
  // geladen (je Verbundkontakt ein Abruf), nicht bei jedem Aufruf der Akte.
  const [verbundNachweise, setVerbundNachweise] = React.useState<VollmachtNachweis[]>([]);
  const verbundGeladen = React.useRef<number | null>(null);
  const unternehmenRef = React.useRef(unternehmen);
  unternehmenRef.current = unternehmen;
  React.useEffect(() => {
    verbundGeladen.current = null;
    setVerbundNachweise([]);
  }, [contactId]);
  const verbundLaden = React.useCallback(async () => {
    if (verbundGeladen.current === contactId) return;
    verbundGeladen.current = contactId;
    const verbund = await fetchUnternehmensverbund(contactId).catch(() => null);
    // Dieselbe Quelle wie „Für alle im Verbund generieren".
    const mitglieder = mitgliederAusUmfeld(contactId, "verbund", null, verbund, unternehmenRef.current ?? []);
    const je = await Promise.all(
      mitglieder.map(async (m) => {
        if (!m.contactId) return [];
        try {
          return nachweiseAusVollmachten(m.contactId, await vollmachtService.liste(m.contactId), { kontaktName: m.name });
        } catch {
          return []; // kein Zugriff oder Server ohne Vollmachten — einfach nicht zeigen
        }
      }),
    );
    if (verbundGeladen.current === contactId) setVerbundNachweise(je.flat());
  }, [contactId]);
  const chipGeoeffnet = (_: unknown, d: { open: boolean }) => {
    if (d.open) void verbundLaden();
  };

  const laden = React.useCallback(async () => {
    try {
      setListe(await vollmachtService.liste(contactId));
      setNichtVerfuegbar(false);
    } catch (err) {
      setListe([]);
      if (err instanceof VollmachtFunktionFehlt) setNichtVerfuegbar(true);
    }
  }, [contactId]);
  React.useEffect(() => {
    void laden();
  }, [laden]);

  const nachArt = new Map((liste ?? []).map((v) => [v.art, v]));
  const status = (art: string): boolean | null => (liste === null || nichtVerfuegbar ? null : liegtVor(nachArt.get(art)));
  const erstinfo = erstinfoNachweis(nachArt.get(VOLLMACHT_STANDARD_KEY), authority);
  // Der Altbestand ist schon vor der Liste bekannt — er darf den Punkt grün machen.
  const erstinfoStatus: boolean | null =
    erstinfo.quelle === "altbestand" ? true : status(VOLLMACHT_STANDARD_KEY);
  // Liegt die Erstinformation nur im Altbestand vor, erzeugt „Herunterladen"
  // einen LEEREN Vordruck — das muss der Menüpunkt sagen.
  const nurAltbestand = erstinfo.quelle === "altbestand";

  const fehlerText = (err: unknown) =>
    err instanceof VollmachtFunktionFehlt
      ? "Diese Funktion ist auf dem Server noch nicht verfügbar."
      : err instanceof Error
        ? err.message
        : "Aktion fehlgeschlagen.";

  /** Vorhandene Vollmacht oder frisch angelegte. */
  const sicherstellen = async (art: string): Promise<VollmachtDto> => {
    const da = nachArt.get(art);
    if (da) return da;
    // Zweite Sicherung neben dem gesperrten Menüpunkt: ohne Schreibrecht wird
    // nie angelegt (das Backend lehnt es ohnehin ab).
    if (!editable) throw new Error(HERUNTERLADEN_GESPERRT_HINWEIS);
    const neu = await vollmachtService.anlegen(contactId, [art]);
    const v = neu.find((x) => x.art === art);
    if (!v) throw new Error("Vollmacht konnte nicht angelegt werden.");
    return v;
  };

  const ausfuehren = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      await laden();
    } catch (err) {
      onFehler(fehlerText(err));
    }
  };

  const herunterladen = (art: string) =>
    void ausfuehren(async () => {
      const v = await sicherstellen(art);
      blobHerunterladen(await vollmachtService.pdf(contactId, v.id), vollmachtDateiname(art, kontaktName));
    });
  // Läuft bis zum `window.open` synchron im Klick — sonst greift der Popup-Blocker.
  const unterschreiben = (art: string) =>
    void ausfuehren(async () => {
      const anzeige = await unterschreibenLassen({
        vollmacht: nachArt.get(art),
        titel: vollmachtLabel(art),
        erzeugen: async () => vollmachtService.signierlink(contactId, (await sicherstellen(art)).id),
      });
      if (anzeige) setSignierlink(anzeige);
    });
  const nachweise = [...nachweiseAusVollmachten(contactId, liste ?? [], { authority }), ...verbundNachweise];
  const ansehen = (n: VollmachtNachweis) => void nachweisOeffnen(n).catch((err) => onFehler(fehlerText(err)));
  /** „Vollmachten ansehen" — nur, wenn etwas vorliegt; bei genau einem Dokument öffnet der Klick direkt. */
  const ansehenPunkt =
    nachweise.length === 0 ? null : nachweise.length === 1 ? (
      <MenuItem data-testid="vollmachten-ansehen" onClick={() => ansehen(nachweise[0])}>
        Vollmachten ansehen
      </MenuItem>
    ) : (
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <MenuItem data-testid="vollmachten-ansehen">Vollmachten ansehen</MenuItem>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            {nachweise.map((n) => (
              <MenuItem key={n.key} data-testid={`vollmacht-nachweis-${n.key}`} onClick={() => ansehen(n)}>
                {nachweisText(n)}
              </MenuItem>
            ))}
          </MenuList>
        </MenuPopover>
      </Menu>
    );
  /** Das Protokoll kommt weiter über den Explorer. */
  const protokollHochladen = () => dateiRef.current?.click();
  const dateiGewaehlt = (e: React.ChangeEvent<HTMLInputElement>) => {
    const datei = e.target.files?.[0];
    e.target.value = "";
    if (!datei) return;
    void ausfuehren(async () => {
      await documentService.upload({ file: datei, contactId, category: "Protokoll", tags: ["protokoll"] });
      onFehler("Protokoll hochgeladen.");
    });
  };
  /**
   * T-42: Vollmacht aus der Dokumentauswahl. Ein KMVS-Dokument wird direkt
   * verknüpft (kein zweiter Upload, Kategorie bleibt), eine Datei vom Rechner
   * wie bisher abgelegt und dann verknüpft.
   */
  const auswahlUebernehmen = (art: string, auswahl: DokumentAuswahlErgebnis) => {
    setAuswahlArt(null);
    if (!auswahlHatInhalt(auswahl)) return;
    void ausfuehren(() =>
      vollmachtAusAuswahlVerknuepfen({ contactId, art, auswahl, vollmacht: () => sicherstellen(art) }),
    );
  };

  /** Zweite Ebene: die Aktionen einer Vollmacht (`kopf`: z. B. „Vollmachten ansehen" obenan). */
  const aktionen = (art: string, kopf?: React.ReactNode) => (
    <MenuList>
      {kopf}
      {herunterladenGesperrt(nachArt.get(art), editable) ? (
        <Tooltip relationship="description" content={HERUNTERLADEN_GESPERRT_HINWEIS}>
          <MenuItem disabled data-testid="vollmacht-herunterladen">
            Herunterladen
          </MenuItem>
        </Tooltip>
      ) : (
        <MenuItem data-testid="vollmacht-herunterladen" onClick={() => herunterladen(art)}>
          {art === VOLLMACHT_STANDARD_KEY && nurAltbestand ? "Leeren Vordruck herunterladen" : "Herunterladen"}
        </MenuItem>
      )}
      <MenuItem disabled={!editable} onClick={() => setAuswahlArt(art)}>
        Hochladen
      </MenuItem>
      <MenuItem
        disabled={unterschreibenGesperrt(nachArt.get(art), editable)}
        onClick={() => unterschreiben(art)}
      >
        Unterschreiben lassen
      </MenuItem>
      {/* Nur bei der Erstinformation und nur, wenn es Haushalt bzw. Verbund gibt. */}
      {art === VOLLMACHT_STANDARD_KEY &&
        sammel.optionen.map((o) => (
          <MenuItem
            key={o.art}
            data-testid={`erstinfo-sammel-${o.art}`}
            disabled={!editable || !!sammel.laeuft}
            onClick={() => void sammel.starten(o.art).then(laden)}
          >
            {sammel.laeuft === o.art ? "Wird erzeugt…" : sammelOptionText(o, SAMMEL_LABEL[o.art])}
          </MenuItem>
        ))}
    </MenuList>
  );

  const weitere = VOLLMACHT_OPTIONEN.filter((o) => o.value !== VOLLMACHT_STANDARD_KEY);
  const hinweisOffline = nichtVerfuegbar ? " (Server kennt die Vollmachten noch nicht)" : "";

  return (
    <>
      {signierlink && <SignierlinkDialog links={[signierlink]} onClose={signierlinkSchliessen} />}
      {auswahlArt && (
        <VollmachtAuswahlDialog
          contactId={contactId}
          kundeFest
          titel={`${vollmachtLabel(auswahlArt)} hinterlegen`}
          onClose={() => setAuswahlArt(null)}
          onUebernehmen={(a) => auswahlUebernehmen(auswahlArt, a)}
        />
      )}
      <input ref={dateiRef} type="file" accept="application/pdf,image/*" style={{ display: "none" }} onChange={dateiGewaehlt} />

      {zeigeErstinfo && (
        <Menu onOpenChange={chipGeoeffnet}>
          <MenuTrigger disableButtonEnhancement>
            <Tooltip
              relationship="description"
              content={(erstinfoStatus === null ? "Wird geladen…" : erstinfoTooltip(erstinfo)) + hinweisOffline}
            >
              <button type="button" className="mchip" data-testid="kopf-erstinfo">
                <Punkt ok={erstinfoStatus} />
                Kundenerstinformation
                <span className="statuspill-caret">▾</span>
              </button>
            </Tooltip>
          </MenuTrigger>
          <MenuPopover>
            {aktionen(VOLLMACHT_STANDARD_KEY, ansehenPunkt)}
          </MenuPopover>
        </Menu>
      )}

      <Menu onOpenChange={chipGeoeffnet}>
        <MenuTrigger disableButtonEnhancement>
          <button type="button" className="mchip" data-testid="kopf-vollmachten">
            weitere Vollmachten
            <span className="statuspill-caret">▾</span>
          </button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            {ansehenPunkt}
            {weitere.map((o) => (
              <Menu key={o.value}>
                <MenuTrigger disableButtonEnhancement>
                  <MenuItem>
                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                      <Punkt ok={status(o.value)} />
                      {o.label}
                    </span>
                  </MenuItem>
                </MenuTrigger>
                <MenuPopover>{aktionen(o.value)}</MenuPopover>
              </Menu>
            ))}
          </MenuList>
        </MenuPopover>
      </Menu>

      {nachOrganigramm}

      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <button type="button" className="mchip" data-testid="kopf-dokumente">
            sonstige Kontaktdokumente
            <span className="statuspill-caret">▾</span>
          </button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem disabled={!editable} onClick={protokollHochladen}>
              Protokoll hochladen
            </MenuItem>
            <MenuItem onClick={onDokumenteOeffnen}>Alle Dokumente anzeigen</MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    </>
  );
}
